import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Stack,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  School as SchoolIcon,
  Tab as TabIcon,
  Notes as NotesIcon,
  Psychology as AIIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { EXTENSION_URLS, APP_STORE_URL, getBrowserLabel } from '../config/extensionUrls';
import api from '../api/axiosConfig';

const steps = [
  {
    title: 'Welcome to Blackmount',
    icon: SchoolIcon,
    content: 'Your AI-powered companion for capturing and organizing knowledge'
  },
  {
    title: 'Save Sessions & Tabs',
    icon: TabIcon,
    content: 'Capture entire research sessions with voice notes, bookmarks, and context'
  },
  {
    title: 'Smart Organization',
    icon: NotesIcon,
    content: 'Transform sessions into projects and organize your knowledge base'
  },
  {
    title: 'AI-Powered Insights',
    icon: AIIcon,
    content: 'Get summaries, ask questions, and discover connections across all your saved content'
  }
];

function WelcomeFlow({ open, onClose }) {
  const [activeStep, setActiveStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!open) {
      setDismissed(false);
      setActiveStep(0);
    }
  }, [open]);

  const dismiss = () => {
    localStorage.removeItem('showWelcomeFlow');
    setDismissed(true);
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const completeOnboarding = async () => {
    try {
      // Mark onboarding as complete
      await api.post(
        '/api/auth/complete-onboarding',
        {},
        {
          headers: {
            Authorization: `Bearer ${await currentUser.getIdToken()}`
          }
        }
      );
      onClose();
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  };

  const handleComplete = () => {
    // Never keep the app hostage to a slow onboarding request. The parent
    // updates profile state only after persistence succeeds.
    dismiss();
    void completeOnboarding();
  };

  const currentStep = steps[activeStep];
  const IconComponent = currentStep.icon;

  return (
    <Dialog
      open={open && !dismissed}
      onClose={null} // Prevent closing by clicking outside
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          overflow: 'visible'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={(activeStep + 1) / steps.length * 100}
          sx={{ height: 4 }}
        />

        {/* Close button */}
        <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
          <IconButton
            aria-label="Close welcome flow"
            onClick={() => {
              dismiss();
              void completeOnboarding();
            }}
            size="small"
            sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: 4, textAlign: 'center', minHeight: 400 }}>
          {/* Icon */}
          <Box sx={{ mb: 3 }}>
            <IconComponent
              sx={{
                fontSize: 80,
                color: 'primary.main',
                filter: 'drop-shadow(0px 4px 8px rgba(37, 99, 235, 0.3))'
              }}
            />
          </Box>

          {/* Content */}
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {currentStep.title}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
            {currentStep.content}
          </Typography>

          {/* Feature highlights for each step */}
          <Card sx={{ mb: 4, bgcolor: 'background.default' }}>
            <CardContent>
              {activeStep === 0 && (
                <Stack spacing={2}>
                  <Typography variant="h6" color="primary.main">
                    Built for Knowledge Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Save research sessions, organize projects, and never lose important insights again.
                  </Typography>
                </Stack>
              )}

              {activeStep === 1 && (
                <Stack spacing={2}>
                  <Typography variant="h6" color="primary.main">
                    Close 40 tabs without losing one
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    One click saves the whole window. Tomorrow — or on another device — reopen the full set exactly as you left it, notes included.
                  </Typography>
                </Stack>
              )}

              {activeStep === 2 && (
                <Stack spacing={2}>
                  <Typography variant="h6" color="primary.main">
                    Project Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Convert sessions to projects, organize bookmarks, and build your personal knowledge base.
                  </Typography>
                </Stack>
              )}

              {activeStep === 3 && (
                <Stack spacing={2}>
                  <Typography variant="h6" color="primary.main">
                    Knowledge Discovery
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Chat with your saved sessions, get AI summaries, and discover insights across your research.
                  </Typography>
                  <Typography variant="subtitle2" sx={{ pt: 1 }}>
                    Get Blackmount everywhere
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                    {['chrome', 'firefox', 'edge'].map((b) => (
                      <Button
                        key={b}
                        size="small"
                        variant="outlined"
                        onClick={() => window.open(EXTENSION_URLS[b], '_blank', 'noopener,noreferrer')}
                      >
                        {getBrowserLabel(b)}
                      </Button>
                    ))}
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer')}
                    >
                      iOS App
                    </Button>
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Step indicator */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((step, index) => (
              <Step key={step.title}>
                <StepLabel>
                  {index < activeStep && <CheckIcon sx={{ color: 'success.main' }} />}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Navigation buttons */}
          <Stack direction="row" spacing={2} justifyContent="center">
            {activeStep > 0 && (
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                Back
              </Button>
            )}

            <Button
              variant="contained"
              onClick={handleNext}
              sx={{ minWidth: 120 }}
            >
              {activeStep === steps.length - 1 ? 'Get Started' : 'Next'}
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default WelcomeFlow;
